import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { UserData } from '../../../user-model';
import { OptionItem, ToggleOption } from '../../../../../../../../ui-components/lib/option.interface';
import {
  LookupFilter,
  LookupOptionItem,
} from '../../../../../../../../ui-components/lib/lookup-field/lookup-field-item';
import { ENDPOINTS } from '../../../../../../../common/constants/endpoints.constants';
import {
  ValidationMessageComponent,
  LookupFieldComponent,
} from '../../../../../../../../ui-components';
import { RadioButtonComponent } from '../../../../../../../../ui-components/lib/radio-button/radio-button.component';
import { PaginatedSelectComponent } from '../../../../../../../../ui-components/lib/paginated-select/paginated-select.component';
import { ModalServiceService } from '../../../../../../../../ui-components/lib/modal/modal-service.service';
import { FeatureFlagsService } from '../../../../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../../../../common/constants/feature-flags.constants';
import { Status } from '../../../../../../../common/enums/user-status';
import {
  PLACEMENT_TYPE_OPTIONS,
  PLACEMENT_TYPE_VALUES,
} from './user-work-info-form-section.constants';
import { DepartmentQuickCreateFormComponent } from './quick-create/department-quick-create-form.component';
import { JobTitleQuickCreateFormComponent } from './quick-create/job-title-quick-create-form.component';
import { PositionQuickCreateFormComponent } from './quick-create/position-quick-create-form.component';

@Component({
  selector: 'lib-user-work-info-form-section',
  imports: [
    TranslocoPipe,
    ReactiveFormsModule,
    ValidationMessageComponent,
    LookupFieldComponent,
    RadioButtonComponent,
    PaginatedSelectComponent,
    DepartmentQuickCreateFormComponent,
    JobTitleQuickCreateFormComponent,
    PositionQuickCreateFormComponent,
  ],
  templateUrl: './user-work-info-form-section.component.html',
  styleUrl: './user-work-info-form-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserWorkInfoFormSectionComponent {
  protected readonly PLACEMENT_TYPE_VALUES = PLACEMENT_TYPE_VALUES;

  readonly formGroup = input.required<FormGroup>();
  readonly userId = input.required<string>();
  readonly legalEntity = input.required<OptionItem[]>();
  readonly department = input.required<OptionItem[]>();
  readonly directManager = input.required<OptionItem[]>();
  readonly userDetails = input.required<UserData | undefined | null>();

  private readonly featureFlagService = inject(FeatureFlagsService);
  private readonly transloco = inject(TranslocoService);
  private readonly modalService = inject(ModalServiceService);

  readonly isUserPlacementTypesEnabled = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.ADMIN.USER_MANAGEMENT.user_placement_types
    )
  ).asReadonly();

  readonly isQuickCreateEnabled = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.ADMIN.USER_MANAGEMENT
        .create_departments_job_titles_and_positions_from_dropdowns
    )
  ).asReadonly();

  /** Pre-placement-types fallback: a single Position picker, shown when `user_placement_types` is off. */
  readonly isSelectThePositionWhenAddingANewUserEnabled = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.ADMIN.USER_MANAGEMENT.select_the_position_when_adding_a_new_user
    )
  ).asReadonly();

  readonly placementTypeOptions = computed<ToggleOption[]>(() =>
    PLACEMENT_TYPE_OPTIONS.map(option => ({
      ...option,
      label: this.transloco.translate(option.label ?? ''),
    }))
  );

  protected readonly departmentApi = `${ENDPOINTS.Lookup.Departments}?filters[state]=active`;
  protected readonly jobTitleApi = `${ENDPOINTS.Lookup.JobTitles}?filters[state]=active`;
  protected readonly directManagerApi = computed(
    () =>
      `${ENDPOINTS.Lookup.User}?filters[not_placement_types][0]=${PLACEMENT_TYPE_VALUES.EXTERNAL}&filters[not_states][0]=${Status.DELETED}${
        this.userId() ? `&filters[not]=${this.userId()}` : ''
      }`
  );

  readonly selectedDepartment = input.required<LookupOptionItem | undefined>();
  readonly selectedDirectManager = input.required<
    LookupOptionItem | undefined
  >();
  readonly selectedJobTitle = input.required<LookupOptionItem | undefined>();

  protected readonly positionApi = ENDPOINTS.Dashboard.Positions.List;
  protected readonly positionFilter: LookupFilter[] = [
    {
      key: 'search',
      value: null,
      value_type: 'search',
      multiple: false,
      required: false,
    },
    {
      key: 'state',
      value: 'active',
    },
    {
      key: 'vacant',
      value: 'true',
    },
  ];
  readonly selectedPosition = model.required<LookupOptionItem | undefined>();

  onSelectPosition(event: LookupOptionItem) {
    const position = {
      id: event.id,
      name: event.name,
      value: String(event.id),
      label: String(event.name),
      legal_entity_name: event['legal_entity']?.name,
      department_name: event['department']?.name,
      job_title_name: event['job_title']?.name,
      direct_manager_name: event['direct_manager']?.name,
    };

    this.selectedPosition.set(position);
  }

  readonly quickCreateSearchText = signal('');

  readonly departmentQuickCreateTpl =
    viewChild<TemplateRef<unknown>>('departmentQuickCreate');
  readonly jobTitleQuickCreateTpl =
    viewChild<TemplateRef<unknown>>('jobTitleQuickCreate');
  readonly positionQuickCreateTpl =
    viewChild<TemplateRef<unknown>>('positionQuickCreate');

  readonly departmentSelect = viewChild<PaginatedSelectComponent>(
    'departmentSelect'
  );
  readonly jobTitleSelect = viewChild<PaginatedSelectComponent>(
    'jobTitleSelect'
  );
  readonly positionLookup = viewChild<LookupFieldComponent>('positionLookup');

  openDepartmentQuickCreate(searchText: string): void {
    this.quickCreateSearchText.set(searchText);
    this.modalService.modalContent.set({
      isOpen: true,
      customizeContent: this.departmentQuickCreateTpl(),
      width: '48',
      modalClass: 'overflow-y-auto',
    });
  }

  openJobTitleQuickCreate(searchText: string): void {
    this.quickCreateSearchText.set(searchText);
    this.modalService.modalContent.set({
      isOpen: true,
      customizeContent: this.jobTitleQuickCreateTpl(),
      width: '48',
      modalClass: 'overflow-y-auto',
    });
  }

  openPositionQuickCreate(searchText: string): void {
    this.quickCreateSearchText.set(searchText);
    this.modalService.modalContent.set({
      isOpen: true,
      customizeContent: this.positionQuickCreateTpl(),
      width: '48',
      modalClass: 'overflow-y-auto',
    });
  }

  onDepartmentCreated(item: LookupOptionItem): void {
    this.departmentSelect()?.selectOption(item, true);
    this.closeQuickCreateModal();
  }

  onJobTitleCreated(item: LookupOptionItem): void {
    this.jobTitleSelect()?.selectOption(item, true);
    this.closeQuickCreateModal();
  }

  onPositionCreated(item: LookupOptionItem): void {
    this.positionLookup()?.selectOption(item, true);
    this.closeQuickCreateModal();
  }

  closeQuickCreateModal(): void {
    this.modalService.modalContent.set({ isOpen: false });
  }
}
